import React, { Fragment } from "react";
import {
  Typography,
  useMediaQuery,
  Theme,
  TableCell,
  TableRow,
  TableBody,
  Box,
  Link,
} from "@mui/material";
import { format, parseJSON } from "date-fns";
import { useTranslation } from "react-i18next";
import styled from "@emotion/styled";
import { Booking, TransactionV2 } from "@bn/api/apiv1";
import { toCurrency } from "@helpers";
import { useInstrumentUrl } from "@hooks";
import { GainLossTypography, TableBodyCell } from "@components";
import { IntervalInfo } from "@features/accounts/account/components/AccountProtocolTable/IntervalInfo";
import { TransactionStatusIcon } from "@features/investments/investment/components/TransactionStatusIcon";

const PurposeText = styled(Typography)`
  word-break: break-all;
`;

const MobileTableBody = styled(TableBody)`
  tr {
    &:nth-of-type(odd) td {
      border: none;
      padding-bottom: 0;
    }
    td {
      padding-left: 0;
      padding-right: 0;
    }
  }
`;

interface AccountProtocolTableBodyProps {
  data: TransactionV2[];
}

const BookingInvestmentLink = ({
  booking,
  variant,
}: {
  booking: Booking;
  variant?: "body1";
}) => {
  const getInstrumentUrl = useInstrumentUrl();
  const instrumentUrl = getInstrumentUrl(booking.investment);
  const name = booking.investment.name;
  return instrumentUrl ? (
    <Link variant={variant} href={instrumentUrl}>
      {name}
    </Link>
  ) : (
    <Typography variant={variant}>{name}</Typography>
  );
};

export const AccountProtocolTableBody = (
  props: AccountProtocolTableBodyProps
) => {
  const { data = [] } = props;

  const { t } = useTranslation();

  const isMoreSM = useMediaQuery((theme: Theme) => theme.breakpoints.up("sm"));

  if (!isMoreSM) {
    return (
      <MobileTableBody>
        {data.map((item) => (
          <Fragment key={item.id}>
            <TableRow>
              <TableBodyCell>
                <Box display="flex" justifyContent="space-between">
                  <Box>
                    <Box display="flex" alignItems="center" gap={2}>
                      {item.booking && (
                        <TransactionStatusIcon
                          dataConsistencyStatus={
                            item.booking?.dataConsistencyStatus
                          }
                          isExternalID={Boolean(item.booking?.externalId)}
                        />
                      )}
                      <Typography variant="body1" color="textSecondary">
                        {format(parseJSON(item.valueDate), "dd.MM.yyyy")}
                      </Typography>
                    </Box>
                    <IntervalInfo
                      isRegular={item.isRegular}
                      paymentInterval={item.paymentInterval}
                      isInterAccountTransfer={item.isInterAccountTransfer}
                      isAdjustingEntry={item.isAdjustingEntry}
                    />
                  </Box>

                  <Box>
                    {item.amount ? (
                      <GainLossTypography
                        variant="subtitle1"
                        noWrap
                        value={item.amount}
                      >
                        {toCurrency(item.amount, "EUR")}
                      </GainLossTypography>
                    ) : (
                      <Typography variant="body1" color="textSecondary">
                        N/A
                      </Typography>
                    )}
                  </Box>
                </Box>
              </TableBodyCell>
            </TableRow>
            <TableRow>
              <TableCell>
                {item.isAdjustingEntry ? (
                  <Typography variant="body1" color="textSecondary">
                    {t("singleAccountPage.protocolTable.adjustingEntryText")}
                  </Typography>
                ) : (
                  <>
                    {item.booking && (
                      <BookingInvestmentLink booking={item.booking} />
                    )}
                    <PurposeText variant="body1" color="textSecondary">
                      {item.purpose ||
                        t(`singleAccountPage.protocolTable.noPurpose`)}
                      {item.literalType && `(Typ: ${item.literalType})`}
                    </PurposeText>
                  </>
                )}
              </TableCell>
            </TableRow>
          </Fragment>
        ))}
      </MobileTableBody>
    );
  }

  return (
    <TableBody>
      {data.map((item) => (
        <TableRow key={item.id} sx={{ verticalAlign: "top" }}>
          <TableBodyCell>
            <Box display="flex" alignItems="center" gap={2}>
              {item.booking && (
                <TransactionStatusIcon
                  dataConsistencyStatus={item.booking?.dataConsistencyStatus}
                  dataSource={item.booking?.dataSource}
                  isExternalID={Boolean(item.booking?.externalId)}
                />
              )}
              <Typography variant="body1" color="textSecondary">
                {format(parseJSON(item.valueDate!), "dd.MM.yyyy")}
              </Typography>
            </Box>
            <IntervalInfo
              isRegular={item.isRegular}
              paymentInterval={item.paymentInterval}
              isInterAccountTransfer={item.isInterAccountTransfer}
              isAdjustingEntry={item.isAdjustingEntry}
            />
          </TableBodyCell>

          <TableBodyCell>
            {item.isAdjustingEntry ? (
              <Typography variant="body1" color="textSecondary">
                {t("singleAccountPage.protocolTable.adjustingEntryText")}
              </Typography>
            ) : (
              <>
                {item.booking && (
                  <BookingInvestmentLink
                    booking={item.booking}
                    variant="body1"
                  />
                )}
                <PurposeText variant="body1" color="textSecondary">
                  {item.purpose ||
                    t(`singleAccountPage.protocolTable.noPurpose`)}{" "}
                  {item.literalType && `(Typ: ${item.literalType})`}
                </PurposeText>
              </>
            )}
          </TableBodyCell>

          <TableBodyCell align="right">
            <Box display="flex" justifyContent="end">
              {typeof item.amount === "number" ? (
                <GainLossTypography
                  variant="subtitle1"
                  noWrap
                  value={item.amount}
                >
                  {toCurrency(item.amount, "EUR")}
                </GainLossTypography>
              ) : (
                "N/A"
              )}
            </Box>
          </TableBodyCell>
        </TableRow>
      ))}
    </TableBody>
  );
};
